import { api } from './index';

import type { CoachAssignment, CoachProfile, User } from '@/types';

export interface CreateCoachAssignmentRequest extends Partial<CoachAssignment> {
  sendEmail?: boolean;
}

export interface BulkCreateCoachAssignmentsResponse {
  coachAssignments: CoachAssignment[];
  emailDelivery?: {
    mode: 'now' | 'scheduled';
    scheduledAt?: string | null;
  } | null;
}

export interface BulkCreateCoachAssignmentsRequest {
  coacheeId: string;
  coachIds: string[];
  sendEmail: boolean;
  delivery?: {
    mode: 'now' | 'scheduled';
    scheduledAt?: string;
    timezone?: string;
  };
}

export interface UpdateCoachProfileRequest {
  id: string;
  coachProfile: Partial<CoachProfile>;
}

export const coachAssignmentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Coach Assignment operations
    createCoachAssignment: builder.mutation<
      CoachAssignment,
      CreateCoachAssignmentRequest
    >({
      invalidatesTags: ['User', 'CoachProfile'],
      query: (coachAssignment) => ({
        data: { coachAssignment },
        method: 'POST',
        url: '/coach_assignments',
      }),
    }),

    bulkCreateCoachAssignments: builder.mutation<
      BulkCreateCoachAssignmentsResponse,
      BulkCreateCoachAssignmentsRequest
    >({
      invalidatesTags: ['User', 'CoachProfile'],
      query: ({ coacheeId, coachIds, sendEmail, delivery }) => ({
        data: {
          coachAssignment: {
            coacheeId,
            coachIds,
            delivery,
            sendEmail,
          },
        },
        method: 'POST',
        url: '/coach_assignments/bulk',
      }),
    }),

    removeCoachAssignment: builder.mutation<unknown, string>({
      invalidatesTags: ['User', 'CoachProfile'],
      query: (id) => ({
        method: 'DELETE',
        url: `/coach_assignments/${id}`,
      }),
    }),

    // Coach Profile operations
    createCoach: builder.mutation<CoachProfile, Partial<User>>({
      invalidatesTags: ['CoachProfile', 'User'],
      query: (user) => ({
        data: user,
        method: 'POST',
        url: '/coach_profiles',
      }),
    }),

    deleteCoachProfile: builder.mutation<void, string>({
      invalidatesTags: ['CoachProfile', 'User'],
      query: (id) => ({
        method: 'DELETE',
        url: `/coach_profiles/${id}`,
      }),
    }),

    updateCoachProfile: builder.mutation<
      CoachProfile,
      UpdateCoachProfileRequest
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'CoachProfile' },
        'CoachProfile',
        'User',
      ],
      query: ({ id, coachProfile }) => {
        const { userId, ...rest } = coachProfile;
        return {
          data: { coachProfile: rest },
          method: 'PUT',
          url: `/coach_profiles/${id}`,
        };
      },
    }),

    republishCoachMatchingData: builder.mutation<unknown, void>({
      invalidatesTags: ['CoachProfile'],
      query: () => ({
        method: 'POST',
        url: '/coach_profiles/republish_matching_data',
      }),
    }),
  }),
});

export const {
  useCreateCoachAssignmentMutation,
  useBulkCreateCoachAssignmentsMutation,
  useRemoveCoachAssignmentMutation,
  useCreateCoachMutation,
  useDeleteCoachProfileMutation,
  useUpdateCoachProfileMutation,
  useRepublishCoachMatchingDataMutation,
} = coachAssignmentApi;

// Backward compatibility wrappers with tracking
export const useCreateCoachAssignmentWithTracking = () => {
  const [trigger, result] = useCreateCoachAssignmentMutation();
  const { track } = require('@/utils');

  return {
    mutate: (
      variables: CreateCoachAssignmentRequest,
      options?: {
        onSuccess?: (data: CoachAssignment) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      track('Assign coach to member');
      trigger(variables)
        .unwrap()
        .then((data: CoachAssignment) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error: unknown) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useRemoveCoachAssignmentWithTracking = () => {
  const [trigger, result] = useRemoveCoachAssignmentMutation();
  const { track } = require('@/utils');

  return {
    mutate: (
      variables: string,
      options?: {
        onSuccess?: (data: unknown) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      track('Remove coach from member');
      trigger(variables)
        .unwrap()
        .then((data: unknown) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error: unknown) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};
